import { createContext, useEffect, useReducer, useState } from "react";
import type { Dispatch } from "react";
import { Locale } from "@dealer-center/ddc-core-utils";
import type { Filters } from "src/utils/navFilterUtils";
import { getFilteredIncentives, getTrimIdList } from "../utils/navFilterUtils";
import { useReadAPI } from "@dealer-center/ddc-pages-incentives-manager-fetch";
import type {
  HttpError,
  IncentivesPayload,
  IncentivesRecord,
  IncentivesState
} from "@dealer-center/ddc-pages-incentives-manager-interface";
import type {
  AccountMappings,
  MakeCodeMap,
  RootNode
} from "@dealer-center/ddc-pages-incentives-manager-lambda";
import { processIncentivesRecord } from "@dealer-center/ddc-pages-incentives-manager-interface";
import type { ControlcenterAPIInterfaceExternal } from "@dealer-center/ddc-pages-controlcenter-interface";
import { isHttpError } from "@dealer-center/ddc-pages-incentives-manager-interface";

interface FiltersProviderProps {
  children: React.ReactElement | React.ReactElement[];
  userInfo: ControlcenterAPIInterfaceExternal;
}

export interface FiltersProviderContextInterface {
  isLoading: boolean;
  error: HttpError | boolean;
  isNavLoading: boolean;
  filters: Filters;
  accountMappings?: AccountMappings | null;
  navtreeData: RootNode | null;
  incentives: IncentivesPayload | null;
  allIncentives?: IncentivesPayload | null;
}

const isMock = new URLSearchParams(window.location.search).has("mock");

export const defaultFilters: Filters = {
  condition: null,
  make: null,
  year: null,
  model: null,
  trim: null,
  years: null,
  type: null,
  source: null,
  freeWords: null,
  scratchText: ""
};

interface FiltersContextType {
  state: FiltersProviderContextInterface;
  dispatch: Dispatch<any>;
  isMakeLocked: (make: string) => boolean;
}

export const FiltersContext = createContext<FiltersContextType>({
  state: {
    filters: defaultFilters,
    accountMappings: null,
    navtreeData: null,
    incentives: null,
    allIncentives: null,
    isLoading: true,
    error: false,
    isNavLoading: true
  },
  dispatch: (() => undefined) as Dispatch<any>,
  isMakeLocked: () => false
});

export function isMakeLocked(
  codeMap: Record<string, MakeCodeMap>,
  make2makeCodeMap: Record<string, string>,
  make: string
): boolean {
  if (!codeMap || !make) {
    return false;
  }
  const makeCode = make2makeCodeMap[make];
  if (codeMap[makeCode]) {
    return codeMap[makeCode].feedLocked ?? false;
  }
  return false;
}

/*
 generated by copilot using prompt: 
"Add a function replaceIncentive that takes an IncentivesPayload and an IncentivesRecord newIncentive and returns a new IncentivesPayload with the specified incentive replaced. If there are no matching incentives then add newIncentive to the DRAFTS logicalState"
*/
export function replaceIncentive(
  payload: IncentivesPayload,
  rawNewIncentive: IncentivesRecord,
  accountId: string,
  lang: string
): IncentivesPayload {
  let incentiveReplaced = false;
  const newIncentive = processIncentivesRecord(
    rawNewIncentive,
    accountId,
    lang
  );

  const updatedLogicalStates = payload.logicalStates.map(state => ({
    ...state,
    incentives: state.incentives.map(incentive => {
      if (
        incentive.content?.incentiveId === newIncentive.content?.incentiveId
      ) {
        incentiveReplaced = true;
        return newIncentive;
      }
      return incentive;
    }),
    totalIncentives: state.incentives.length
  }));

  if (!incentiveReplaced) {
    const draftsState = updatedLogicalStates.find(
      state => state.logicalState === "DRAFTS"
    );
    if (draftsState) {
      draftsState.incentives.push(newIncentive);
      draftsState.totalIncentives = draftsState.incentives.length;
    } else {
      updatedLogicalStates.push({
        logicalState: "DRAFTS",
        totalIncentives: 1,
        incentives: [newIncentive]
      });
    }
  }

  return { logicalStates: updatedLogicalStates };
}

function incentiveChanged(
  state: FiltersProviderContextInterface,
  incentive: IncentivesRecord,
  accountId: string,
  lang: string
) {
  if (!state || !state.allIncentives || !incentive) {
    return state;
  }
  return {
    ...state,
    allIncentives: replaceIncentive(
      state.allIncentives,
      incentive,
      accountId,
      lang
    )
  };
}

export function removeIncentives(
  state: FiltersProviderContextInterface,
  incentiveIds: string[],
  fromIdx: number
): FiltersProviderContextInterface {
  const { allIncentives } = state;
  if (!allIncentives || !allIncentives.logicalStates) {
    return state;
  }

  const logicalStates = allIncentives.logicalStates.map((item, idx) => {
    if (idx === fromIdx) {
      const filteredIncentives = item.incentives.filter(
        inc => !incentiveIds.includes(inc.content.incentiveId || "")
      );
      return {
        ...item,
        incentives: filteredIncentives,
        totalIncentives: filteredIncentives.length
      };
    }
    return item;
  });

  return {
    ...state,
    allIncentives: {
      ...allIncentives,
      logicalStates
    }
  };
}

export function moveIncentives(
  state: FiltersProviderContextInterface,
  incentiveIds: string[],
  fromState: string,
  toState: string
): FiltersProviderContextInterface {
  const { allIncentives } = state;

  if (!allIncentives || !allIncentives.logicalStates) {
    return state;
  }

  const fromIdx = allIncentives.logicalStates.findIndex(
    (item: IncentivesState) => item.logicalState === fromState
  );
  if (fromIdx === -1) {
    return state;
  }

  if (toState === "NONE") {
    return removeIncentives(state, incentiveIds, fromIdx);
  }

  const toIdx = allIncentives.logicalStates.findIndex(
    (item: IncentivesState) => item.logicalState === toState
  );
  if (toIdx === -1) {
    return state;
  }

  const toIncentives: IncentivesRecord[] = [
    ...allIncentives.logicalStates[toIdx].incentives
  ];
  const fromIncentives: IncentivesRecord[] = [];
  allIncentives?.logicalStates[fromIdx].incentives.forEach(
    (item: IncentivesRecord) => {
      if (incentiveIds.includes(item.content.incentiveId || "")) {
        toIncentives.push(item);
      } else {
        fromIncentives.push(item);
      }
    }
  );
  if (
    toIncentives.length > allIncentives.logicalStates[toIdx].incentives.length
  ) {
    // TODO: do we need to sort the incentives in the toIncetives list? See CC6
    const newState = {
      ...state,
      allIncentives: {
        ...state.allIncentives,
        logicalStates: allIncentives.logicalStates.map(
          (item: IncentivesState) => {
            if (item.logicalState === fromState) {
              return {
                ...item,
                incentives: fromIncentives,
                totalIncentives: fromIncentives.length
              };
            } else if (item.logicalState === toState) {
              return {
                ...item,
                incentives: toIncentives,
                totalIncentives: toIncentives.length
              };
            }
            return item;
          }
        )
      }
    };
    return newState;
  }
  return state;
}

export function deleteOrDeclineIncentives(
  state: FiltersProviderContextInterface,
  incentiveIds: string[],
  fromState: string,
  accountId: string
): FiltersProviderContextInterface {
  const { allIncentives } = state;

  if (!allIncentives || !allIncentives.logicalStates) {
    return state;
  }
  const fromIdx = allIncentives.logicalStates.findIndex(
    (item: IncentivesState) => item.logicalState === fromState
  );
  if (fromIdx === -1) {
    return state; // fromState not found
  }

  // Find or create the DECLINED state
  let declinedIdx = allIncentives.logicalStates.findIndex(
    (item: IncentivesState) => item.logicalState === "DECLINED"
  );
  let declinedState = allIncentives.logicalStates[declinedIdx];
  if (declinedIdx === -1) {
    declinedState = {
      logicalState: "DECLINED",
      totalIncentives: 0,
      incentives: []
    };
    allIncentives.logicalStates.push(declinedState);
    declinedIdx = allIncentives.logicalStates.length - 1;
  }

  const fromIncentives: IncentivesRecord[] = [];
  const declinedIncentives: IncentivesRecord[] = [...declinedState.incentives];

  allIncentives.logicalStates[fromIdx].incentives.forEach(
    (item: IncentivesRecord) => {
      const shouldDecline =
        incentiveIds.includes(item.content.incentiveId || "") &&
        item.creatorAccountId !== accountId;
      if (shouldDecline) {
        declinedIncentives.push(item);
      } else if (!incentiveIds.includes(item.content.incentiveId || "")) {
        fromIncentives.push(item);
      }
      // else: deleted (not pushed anywhere)
    }
  );

  const newLogicalStates = allIncentives.logicalStates.map(
    (item: IncentivesState, idx: number) => {
      if (idx === fromIdx) {
        return {
          ...item,
          incentives: fromIncentives,
          totalIncentives: fromIncentives.length
        };
      }
      if (idx === declinedIdx) {
        return {
          ...item,
          incentives: declinedIncentives,
          totalIncentives: declinedIncentives.length
        };
      }
      return item;
    }
  );

  return {
    ...state,
    allIncentives: {
      ...state.allIncentives,
      logicalStates: newLogicalStates
    }
  };
}

function reducer(state, action) {
  switch (action.type) {
    case "INITIALIZE_INCENTIVES":
      return {
        ...state,
        allIncentives: action.incentives,
        incentives: action.incentives,
        error: false,
        isLoading: false
      };
    case "INITIALIZE_NAVTREE":
      return {
        ...state,
        navtreeData: action.navtreeData,
        isNavLoading: false
      };
    case "INITIALIZE_DATA":
      return {
        ...state,
        allIncentives: action.incentives,
        incentives: action.incentives,
        navtreeData: action.navtreeData,
        error: false
      };
    case "SET_INCENTIVES":
      return {
        ...state,
        incentives: action.incentives,
        isLoading: !state.error && !state.accountMappings
      };
    case "SET_FILTERS":
      return {
        ...state,
        filters: action.filters
      };
    case "SET_ACCOUNT_MAPPINGS":
      return {
        ...state,
        accountMappings: action.accountMappings,
        isLoading: !state.error && !state.incentives
      };
    case "SET_ERROR":
      return {
        ...state,
        error: action.error,
        isLoading: false
      };
    case "INCENTIVE_CHANGED":
      return incentiveChanged(
        state,
        action.incentive,
        action.accountId,
        action.lang
      );
    case "MOVE_INCENTIVES":
      return moveIncentives(
        state,
        action.incentiveIds,
        action.fromState,
        action.toState
      );
    case "DELETE_INCENTIVES":
      return deleteOrDeclineIncentives(
        state,
        action.incentiveIds,
        action.fromState,
        action.accountId
      );
    default:
      return state;
  }
}

export const FiltersProvider = ({
  children,
  userInfo
}: FiltersProviderProps) => {
  const [navigationParams, setNavigationParams] = useState("");
  const [state, dispatch] = useReducer(reducer, {
    filters: defaultFilters,
    allIncentives: null,
    incentives: null,
    navtreeData: null,
    isLoading: true,
    isNavLoading: true,
    error: false
  } as FiltersProviderContextInterface);
  const { filters, allIncentives, navtreeData } = state;
  const [startTime] = useState(performance.now());

  const {
    isLoading: isAccountMappingsLoading,
    isError: isAccountMappingsError,
    data: accountMappingsData
  } = useReadAPI({
    resource: "account-mappings",
    asAccount: userInfo.accountId,
    asUser: userInfo.userId,
    locale: Locale.getCurrent().replace("-", "_"),
    isMock
  });

  const {
    isLoading: isNavigationLoading,
    isError: isNavigationError,
    data: navTree
  } = useReadAPI({
    resource: "navigation",
    asAccount: userInfo.accountId,
    asUser: userInfo.userId,
    extraURLParams: navigationParams,
    disabled: navigationParams === "",
    locale: Locale.getCurrent().replace("-", "_"),
    isMock
  });

  const {
    isLoading: isReadLoading,
    isError,
    data
  } = useReadAPI({
    resource: "incentives",
    asAccount: userInfo.accountId,
    asUser: userInfo.userId,
    locale: Locale.getCurrent().replace("-", "_"),
    isMock
  });

  const hasNoFilter = (filters: Filters) => {
    return (
      !filters.condition &&
      !filters.make &&
      !filters.year &&
      !filters.model &&
      !filters.trim &&
      !filters.years &&
      !filters.freeWords &&
      !filters.type &&
      !filters.source
    );
  };

  useEffect(() => {
    if (
      accountMappingsData &&
      !isAccountMappingsError &&
      !isAccountMappingsLoading
    ) {
      if (isHttpError(accountMappingsData)) {
        dispatch({
          type: "SET_ERROR",
          error: accountMappingsData as HttpError
        });
      } else {
        dispatch({
          type: "SET_ACCOUNT_MAPPINGS",
          accountMappings: accountMappingsData
        });
      }
    }
  }, [isAccountMappingsLoading, accountMappingsData, isAccountMappingsError]);

  useEffect(() => {
    if (data && !isError && !isReadLoading) {
      if (isHttpError(data)) {
        dispatch({ type: "SET_ERROR", error: data as HttpError });
      } else {
        const incentives = data as IncentivesPayload;
        dispatch({ type: "INITIALIZE_INCENTIVES", incentives });
        console.log(
          `Incentives loaded in ${Math.round(performance.now() - startTime)}ms`
        );
      }
    }
  }, [data, isError, isReadLoading]);

  useEffect(() => {
    if (allIncentives && accountMappingsData && !navTree) {
      const currentYear = new Date().getFullYear();
      const outsideMakeYears = {};
      const addMakeYear = (make: string, year: number) => {
        if (!outsideMakeYears[make]) {
          outsideMakeYears[make] = [];
        }
        if (!outsideMakeYears[make].includes(year)) {
          outsideMakeYears[make].push(year);
        }
      };
      allIncentives.logicalStates.forEach(state => {
        state.incentives.forEach(incentive => {
          if (incentive.applicableYear !== -1) {
            if (
              incentive.applicableYear < currentYear - 1 ||
              incentive.applicableYear > currentYear + 1
            ) {
              addMakeYear(incentive.applicableMake, incentive.applicableYear);
            }
          } else {
            incentive.applicableVehicleList.forEach(vehicle => {
              if (
                vehicle.year < currentYear - 1 ||
                vehicle.year > currentYear + 1
              ) {
                addMakeYear(vehicle.make, vehicle.year);
              }
            });
          }
        });
      });
      const groupMatchingYears = {};
      for (const make in outsideMakeYears) {
        const yearsStr = outsideMakeYears[make].join(",");
        if (!groupMatchingYears[yearsStr]) {
          groupMatchingYears[yearsStr] = [];
        }
        groupMatchingYears[yearsStr].push(make);
      }

      let extraYears = "";
      Object.keys(groupMatchingYears).forEach(years => {
        const makes = groupMatchingYears[years].join(",");
        const yearsMakes = `${makes}~${years}`;
        if (extraYears.length > 0) {
          extraYears += ";";
        }
        extraYears += yearsMakes;
      });
      const makeList = accountMappingsData.accountMakes.join(",");
      setNavigationParams(`&makes=${makeList}&extraYears=${extraYears}`);
    }
  }, [allIncentives, accountMappingsData, navTree]);

  useEffect(() => {
    if (navTree && !isNavigationError && !isNavigationLoading) {
      if (isHttpError(navTree)) {
        dispatch({ type: "SET_ERROR", error: navTree as HttpError });
      } else {
        dispatch({ type: "INITIALIZE_NAVTREE", navtreeData: navTree });
      }
    }
  }, [navTree, isNavigationError, isNavigationLoading]);

  useEffect(() => {
    const makeFilteredIncentives = (incentives: IncentivesRecord[]) => {
      const trimIdList = getTrimIdList(navtreeData, filters);
      const filteredIncentives = getFilteredIncentives(
        incentives,
        trimIdList,
        filters
      );
      return filteredIncentives;
    };
    if (allIncentives && navtreeData) {
      if (hasNoFilter(filters)) {
        dispatch({ type: "SET_INCENTIVES", incentives: allIncentives });
      } else {
        let onlyMake = filters.make;
        let hasOneMake = filters.make === null;
        const logicalStates = allIncentives.logicalStates.map(
          (item: IncentivesState) => {
            const newIncentives = makeFilteredIncentives(item.incentives);
            if (hasOneMake) {
              newIncentives.some(incentive => {
                if (
                  hasOneMake &&
                  (!onlyMake || incentive.applicableMake === onlyMake)
                ) {
                  onlyMake = incentive.applicableMake;
                  return false; // continue searching
                } else {
                  onlyMake = null;
                  hasOneMake = false;
                  return true; // stop searching
                }
              });
            }
            const filteredItem = {
              ...item,
              incentives: newIncentives,
              totalIncentives: newIncentives.length
            };
            return filteredItem;
          }
        );
        dispatch({
          type: "SET_INCENTIVES",
          incentives: { logicalStates }
        });
        if (onlyMake && filters.make !== onlyMake) {
          dispatch({
            type: "SET_FILTERS",
            filters: { ...filters, make: onlyMake }
          });
        }
      }
    }
  }, [filters, allIncentives, navtreeData]);

  return (
    <FiltersContext.Provider
      value={{
        state,
        dispatch,
        isMakeLocked: (make: string) => {
          if (state.accountMappings && state.accountMappings.makeCodeMap) {
            return isMakeLocked(
              state.accountMappings.makeCodeMap,
              state.accountMappings.make2makeCodeMap,
              make
            );
          }
          return false;
        }
      }}
    >
      {children}
    </FiltersContext.Provider>
  );
};
